import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { formatPriceWithSymbol, getCurrencySymbol } from '../../../utils/currency';
import './style.css';

const ModalDesconto = ({ isOpen, onClose, onApply, subtotal, currentDiscount = 0 }) => {
    const { t } = useTranslation();
    const [mode, setMode] = useState('fixed'); // 'fixed' or 'percentage'
    const [inputValue, setInputValue] = useState('');
    const [error, setError] = useState('');
    const inputRef = useRef(null);

    useEffect(() => {
        if (isOpen) {
            setError('');
            if (currentDiscount > 0) {
                setMode('fixed');
                setInputValue(String(currentDiscount));
            } else {
                setInputValue('');
            }
            setTimeout(() => inputRef.current?.focus(), 100);
        }
    }, [isOpen, currentDiscount]);

    if (!isOpen) return null;

    const safeSubtotal = Number(subtotal) || 0;

    const getDiscountValue = () => {
        const val = parseFloat(inputValue);
        if (isNaN(val) || val <= 0) return 0;
        if (mode === 'percentage') {
            return Math.round((safeSubtotal * val / 100) * 100) / 100;
        }
        return val;
    };

    const discountValue = getDiscountValue();
    const newTotal = Math.max(0, safeSubtotal - discountValue);

    const handleApply = () => {
        const val = parseFloat(inputValue);
        if (isNaN(val) || val <= 0) {
            setError(t('pdv:modals.discount.invalidValue'));
            return;
        }
        if (discountValue > safeSubtotal) {
            setError(t('pdv:modals.discount.exceedsSubtotal'));
            return;
        }
        setError('');
        onApply(discountValue);
        onClose();
    };

    const handleRemove = () => {
        onApply(0);
        onClose();
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') handleApply();
        if (e.key === 'Escape') onClose();
    };

    // Renderiza via portal no document.body para escapar do stacking context
    // criado pelo ModalWrapper do ModalPdv (que usa `transform` na animação de slide).
    // Sem isso, este overlay `position: fixed` fica preso dentro do painel da direita
    // e o modal de adicionais (react-bootstrap, portado para o body) aparece por cima.
    return createPortal(
        <div className="modal-desconto-overlay" onClick={onClose}>
            <div className="modal-desconto-container" onClick={(e) => e.stopPropagation()}>
                <h3 className="modal-desconto-title">{t('pdv:modals.discount.title')}</h3>

                <div className="modal-desconto-mode-toggle">
                    <button
                        className={`mode-btn ${mode === 'fixed' ? 'active' : ''}`}
                        onClick={() => { setMode('fixed'); setInputValue(''); setError(''); }}
                    >
                        {t('pdv:modals.discount.fixedValue')}
                    </button>
                    <button
                        className={`mode-btn ${mode === 'percentage' ? 'active' : ''}`}
                        onClick={() => { setMode('percentage'); setInputValue(''); setError(''); }}
                    >
                        {t('pdv:modals.discount.percentage')}
                    </button>
                </div>

                <div className="modal-desconto-input-group">
                    <span className="input-prefix">{mode === 'fixed' ? getCurrencySymbol() : '%'}</span>
                    <input
                        ref={inputRef}
                        type="number"
                        min="0"
                        step="0.01"
                        className="modal-desconto-input"
                        placeholder={mode === 'fixed' ? t('pdv:modals.discount.enterValue') : t('pdv:modals.discount.enterPercentage')}
                        value={inputValue}
                        onChange={(e) => { setInputValue(e.target.value); setError(''); }}
                        onKeyDown={handleKeyDown}
                    />
                </div>

                {error && <p className="modal-desconto-error">{error}</p>}

                <div className="modal-desconto-preview">
                    <div className="preview-row">
                        <span>{t('pdv:modals.discount.currentSubtotal')}</span>
                        <span>{formatPriceWithSymbol(safeSubtotal)}</span>
                    </div>
                    {discountValue > 0 && (
                        <>
                            <div className="preview-row discount-row">
                                <span>{t('pdv:modals.discount.discountPreview')}</span>
                                <span>-{formatPriceWithSymbol(discountValue)}</span>
                            </div>
                            <div className="preview-row total-row">
                                <span>{t('pdv:modals.discount.newTotal')}</span>
                                <span>{formatPriceWithSymbol(newTotal)}</span>
                            </div>
                        </>
                    )}
                </div>

                <div className="modal-desconto-actions">
                    <button className="btn-cancel" onClick={onClose}>
                        {t('pdv:modals.discount.cancel')}
                    </button>
                    {currentDiscount > 0 && (
                        <button className="btn-remove" onClick={handleRemove}>
                            {t('pdv:modals.discount.remove')}
                        </button>
                    )}
                    <button className="btn-apply" onClick={handleApply} disabled={!inputValue || discountValue <= 0}>
                        {t('pdv:modals.discount.apply')}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default ModalDesconto;
